import type { WebLocale } from '@/enums/web-locale';

import { getUserCentricsConsentProfile } from '@/constants/user-centrics';
import { GDPR_WEB_LOCALES } from '@/constants/web-locales.constant';
import { storeUserGeoLocation } from '@/helpers/geo-location-helper';
import { hasUCConsentBanner } from '@/helpers/tracking';
import { UsercentricsHelper } from '@/helpers/usercentrics-helper';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';

function startUsercentrics(webLocale: WebLocale) {
	// This registers the window event listener and needs to run very early to catch events
	UsercentricsHelper.registerAndTrackUsercentricEvents(useUserStore().saveDPSConsents);
	UsercentricsHelper.preload(getUserCentricsConsentProfile(webLocale)).catch(() => {});
}

export default defineNuxtPlugin({
	parallel: true,
	// the web locale is resolved in core-env (which itself waits for pinia-init), so it must have run
	// before the locale is read below. Otherwise a client-only build would still see the default locale.
	dependsOn: ['core-env'],
	async setup() {
		const webLocale = useLanguageStore().webLocale;

		// GDPR locales always need the banner, regardless of geo, so start loading Usercentrics right away
		// instead of waiting for the geo-location request. The geo request runs in parallel.
		// GDPR locales always use the TCF profile, so the profile doesn't depend on geo either.
		const isGdprLocale = GDPR_WEB_LOCALES.includes(webLocale);

		if (isGdprLocale) startUsercentrics(webLocale);

		await storeUserGeoLocation();

		// Non-GDPR locales only show the banner for CCPA subdivisions, which requires the geo result.
		if (!isGdprLocale && hasUCConsentBanner(webLocale)) startUsercentrics(webLocale);

		import('@/helpers/bundle-size-monitor').then(({ initBundleSizeMonitor }) => initBundleSizeMonitor());
	},
});
